import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ReachProSupportClient,
  ReachProSupportConversation,
  ReachProSupportMessage,
} from 'src/WebApiController';

import { CONVERSATIONS_QUERY_KEY } from './useReachProConversations';

const MESSAGES_QUERY_KEY = 'reachpro-messages';

export function useReachProSendMessage(conversationId: string) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { loginContext } = useAppContext();
  const { trackError } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  return useMutation({
    mutationFn: async (messageContent: string) => {
      if (!hasReachProSupportFeature) {
        throw new Error('Support Center feature is not enabled');
      }

      return tryInvokeApi(
        async () => {
          return await new ReachProSupportClient(
            activeAccountWebClientConfig
          ).sendMessage({
            conversationId: parseInt(conversationId),
            messageContent,
          });
        },
        (error) => {
          trackError('ReachProSupportClient.sendMessage', error);
        }
      );
    },

    onMutate: async (messageContent: string) => {
      await queryClient.cancelQueries({
        queryKey: [MESSAGES_QUERY_KEY, conversationId],
      });

      const previousMessages = queryClient.getQueryData<
        ReachProSupportMessage[]
      >([MESSAGES_QUERY_KEY, conversationId]);

      const tempId = -Date.now();

      queryClient.setQueryData<ReachProSupportMessage[]>(
        [MESSAGES_QUERY_KEY, conversationId],
        (old = []) => {
          const tempMessage: ReachProSupportMessage = {
            workItemNoteId: tempId,
            messageContent,
            senderName: loginContext?.user?.activeAccount?.accountName ?? '',
            timestamp: new Date().toISOString(),
            isSent: true,
          };
          return [...old, tempMessage];
        }
      );

      queryClient.setQueryData<ReachProSupportConversation[]>(
        [CONVERSATIONS_QUERY_KEY, activeAccountWebClientConfig.activeAccountId],
        (old = []) =>
          old.map((conv) => {
            const now = new Date().toISOString();
            return conv.conversationId.toString() === conversationId
              ? {
                  ...conv,
                  lastMessageSnippet: messageContent.substring(0, 200),
                  lastReadTimestamp: now,
                  lastMessageTimestamp: now,
                  hasUnreadMessages: false,
                }
              : conv;
          })
      );

      return { previousMessages, tempId };
    },

    onSuccess: (
      serverMessage: ReachProSupportMessage | null,
      _variables,
      context
    ) => {
      if (!serverMessage) return;

      queryClient.setQueryData<ReachProSupportMessage[]>(
        [MESSAGES_QUERY_KEY, conversationId],
        (old = []) =>
          old.map((m) =>
            m.workItemNoteId === context.tempId ? serverMessage : m
          )
      );

      queryClient.setQueryData<ReachProSupportConversation[]>(
        [CONVERSATIONS_QUERY_KEY, activeAccountWebClientConfig.activeAccountId],
        (old = []) => {
          return old.map((conv) =>
            conv.conversationId.toString() === conversationId
              ? {
                  ...conv,
                  lastMessageSnippet: serverMessage.messageContent.substring(
                    0,
                    200
                  ),
                  lastReadTimestamp: serverMessage.timestamp,
                  lastMessageTimestamp: serverMessage.timestamp,
                  hasUnreadMessages: false,
                }
              : conv
          );
        }
      );

      const queryClientData = queryClient.getQueryData<
        ReachProSupportConversation[]
      >([
        CONVERSATIONS_QUERY_KEY,
        activeAccountWebClientConfig.activeAccountId,
      ]);
    },

    onError: (
      _error: unknown,
      _variables: string,
      context:
        | { previousMessages?: ReachProSupportMessage[]; tempId?: number }
        | undefined
    ) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(
          [MESSAGES_QUERY_KEY, conversationId],
          context.previousMessages
        );
      }
    },
  });
}
